import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { LyricTimingLine } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { LyricTimingRow } from './LyricTimingRow';
import { lyricTimingMessages } from './lyricTimingMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric timing; each part uses its own slice of it */
import styles from './LyricTiming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricTimingRowsProps {
  lines: LyricTimingLine[];
  /** The line the master plays now, or null. */
  nowIndex: number | null;
  /** True while the master plays: the list follows the line that plays. */
  isPlaying: boolean;
  selected: number | null;
  canEdit: boolean;
  canPlay: boolean;
  /** The editor page shows every line; the sheet scrolls the list. */
  isPage: boolean;
  onPlay: (index: number) => void;
  onSelect: (index: number) => void;
  onNudge: (index: number, deltaMs: number) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}

/** Scrolls the row of `index` into the list, if the browser can. */
function revealRow(list: HTMLElement | null, index: number | null): void {
  if (list === null || index === null) {
    return;
  }
  const row = list.querySelector<HTMLElement>(`[data-line-index="${index}"]`);
  if (row !== null && typeof row.scrollIntoView === 'function') {
    row.scrollIntoView({ block: 'nearest' });
  }
}

/**
 * The lines of the sync editor (design s8). The list takes the focus and the keys
 * (`useLyricEditorKeys`). While the master plays, the list follows the line that plays; the arrow
 * keys bring the selected line into view.
 */
const LyricTimingRowsComponent: React.FC<LyricTimingRowsProps> = ({
  lines,
  nowIndex,
  isPlaying,
  selected,
  canEdit,
  canPlay,
  isPage,
  onPlay,
  onSelect,
  onNudge,
  onKeyDown,
}) => {
  const intl = useIntl();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPlaying) {
      revealRow(listRef.current, nowIndex);
    }
  }, [isPlaying, nowIndex]);

  useEffect(() => {
    revealRow(listRef.current, selected);
  }, [selected]);

  return (
    // The list is one tab stop; the keys of the editor act on the selected line.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the list takes the editor keys
    <div
      ref={listRef}
      className={clsx(styles.rows, isPage && styles.rowsPage)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the list takes the editor keys
      tabIndex={0}
      role="group"
      aria-label={intl.formatMessage(lyricTimingMessages.listLabel)}
      onKeyDown={onKeyDown}
      data-testid="lyric-rows"
    >
      {lines.map((line) => (
        <LyricTimingRow
          key={line.index}
          line={line}
          isNow={nowIndex === line.index}
          isSelected={selected === line.index}
          canEdit={canEdit}
          canPlay={canPlay}
          onPlay={onPlay}
          onSelect={onSelect}
          onNudge={onNudge}
        />
      ))}
    </div>
  );
};

export const LyricTimingRows = TypedMemo(LyricTimingRowsComponent);
